/**
 * In-browser request router for ETrax demo mode.
 *
 * Handles every `/api/*` request the frontend makes, against an in-memory copy
 * of the demo dataset. Mutations (checkout / checkin / status changes) persist
 * for the session and reset on a full page reload.
 *
 * The router NEVER returns a 4xx/5xx for a matched-but-empty case — unmatched
 * routes fall through to a benign 200 so the demo never surfaces error toasts
 * or the 401 → /login redirect baked into the real API client.
 */
import {
  createDemoState,
  DemoState,
  DemoEquipment,
  DEMO_TOKEN,
  DEMO_REFRESH_TOKEN,
} from './data';

let state: DemoState = createDemoState();

export function resetDemoState() {
  state = createDemoState();
}

export interface DemoResponse {
  status: number;
  data: any;
  /** When set, the fetch interceptor returns `data` as-is (not JSON) with this content type. */
  contentType?: string;
}

const ok = (data: any): DemoResponse => ({ status: 200, data });
const raw = (data: string, contentType: string): DemoResponse => ({ status: 200, data, contentType });

/**
 * The two pages families consume different status vocabularies:
 *  - axios/service callers (Equipment list + detail) use the shared HEALTH enum
 *    stored on the record (GREEN/YELLOW/RED/RETIRED/LOST).
 *  - raw-fetch callers (Scan/Voice/Reports) use OPERATIONAL availability
 *    (AVAILABLE/CHECKED_OUT/MAINTENANCE).
 * We project the same records into whichever vocabulary the caller expects.
 */
export type Transport = 'axios' | 'fetch';

function operationalStatus(e: DemoEquipment): string {
  if (e.checkedOutTo) return 'CHECKED_OUT';
  if (e.status === 'RED' || e.status === 'RETIRED' || e.status === 'LOST' || e.condition === 'DAMAGED') {
    return 'MAINTENANCE';
  }
  return 'AVAILABLE';
}

function projectEquipment(e: DemoEquipment, transport: Transport): any {
  if (transport === 'fetch') return { ...e, status: operationalStatus(e) };
  return e;
}

function authPayload() {
  return {
    user: state.user,
    tokens: { accessToken: DEMO_TOKEN, refreshToken: DEMO_REFRESH_TOKEN },
    // superset so both the Login page ({tokens,user}) and AuthProvider ({data:{...}}) work
    data: {
      user: state.user,
      tokens: { accessToken: DEMO_TOKEN, refreshToken: DEMO_REFRESH_TOKEN },
    },
  };
}

function filterEquipment(params: URLSearchParams): { equipment: DemoEquipment[]; total: number; page: number; limit: number } {
  let list = [...state.equipment];

  const search = params.get('search') || params.get('q');
  if (search) {
    const q = search.toLowerCase();
    list = list.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.qrCode.toLowerCase().includes(q) ||
        e.brand.toLowerCase().includes(q) ||
        e.category.toLowerCase().includes(q) ||
        (e.serialNumber || '').toLowerCase().includes(q),
    );
  }

  const statuses = params.getAll('status');
  if (statuses.length) list = list.filter((e) => statuses.includes(e.status));

  const categoryId = params.get('categoryId');
  if (categoryId) list = list.filter((e) => e.categoryId === categoryId);

  const locationId = params.get('locationId');
  if (locationId) list = list.filter((e) => e.locationId === locationId);

  const sortBy = params.get('sortBy') as keyof DemoEquipment | null;
  const sortOrder = params.get('sortOrder') === 'desc' ? -1 : 1;
  if (sortBy) {
    list.sort((a, b) => {
      const av = a[sortBy] as any;
      const bv = b[sortBy] as any;
      if (av == null) return 1;
      if (bv == null) return -1;
      return av > bv ? sortOrder : av < bv ? -sortOrder : 0;
    });
  }

  const total = list.length;
  const page = parseInt(params.get('page') || '1', 10);
  const limit = parseInt(params.get('limit') || '100', 10);
  const start = (page - 1) * limit;
  return { equipment: list.slice(start, start + limit), total, page, limit };
}

function dashboard() {
  const eq = state.equipment;
  const total = eq.length;
  const checkedOut = eq.filter((e) => e.checkedOutTo).length;
  const overdue = eq.filter((e) => e.checkedOutTo && e.dueDate && new Date(e.dueDate) < new Date()).length;
  // Count via the same operational projection the Reports/Scan pages use, so the
  // dashboard tiles and the reports tiles agree on the same records.
  const maintenance = eq.filter((e) => operationalStatus(e) === 'MAINTENANCE').length;
  const damaged = eq.filter((e) => e.condition === 'DAMAGED' || e.status === 'RED').length;
  const available = eq.filter((e) => operationalStatus(e) === 'AVAILABLE').length;
  const utilizationRate = Math.round((checkedOut / total) * 100);
  const recentlyAdded = 6;

  const checkoutCounts: Record<string, number> = {};
  state.transactions
    .filter((t) => t.type === 'CHECK_OUT')
    .forEach((t) => {
      checkoutCounts[t.equipmentId] = (checkoutCounts[t.equipmentId] || 0) + 1;
    });
  // Synthesize believable popularity even where transaction history is sparse.
  const topEquipment = eq
    .map((e, i) => ({
      id: e.id,
      name: e.name,
      code: e.qrCode,
      checkoutCount: (checkoutCounts[e.id] || 0) + ((eq.length - i) % 7) + 3,
    }))
    .sort((a, b) => b.checkoutCount - a.checkoutCount)
    .slice(0, 5);

  // Six months of sample utilization ending at the current month, derived from
  // today's real counts with a small deterministic wobble — never a fixed year.
  const utilizationTrends = Array.from({ length: 6 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - (5 - i));
    const label = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const wobble = ((i * 7) % 5) - 2;
    return {
      date: label,
      available: Math.max(0, available + wobble - i),
      checkedOut: Math.max(0, checkedOut - wobble + i),
      maintenance: Math.max(0, maintenance + ((i % 3) - 1)),
    };
  });

  return {
    overview: {
      totalEquipment: total,
      availableEquipment: available,
      checkedOutEquipment: checkedOut,
      overdueEquipment: overdue,
      maintenanceEquipment: maintenance,
      damagedEquipment: damaged,
      utilizationRate,
      recentlyAdded,
    },
    utilizationTrends,
    activity: {
      recentTransactions: state.transactions.filter((t) => new Date(t.createdAt) > new Date(Date.now() - 7 * 864e5)).length,
      pendingMaintenance: eq.filter((e) => e.status === 'RED').length,
      transactionTrend: 12,
    },
    topEquipment,
    recentActivity: state.transactions.slice(0, 8).map((t) => ({
      id: t.id,
      type: t.type,
      equipmentName: t.equipmentName,
      equipmentCode: t.equipmentCode,
      userName: t.userName,
      createdAt: t.createdAt,
    })),
    generatedAt: new Date().toISOString(),
  };
}

function statistics() {
  const eq = state.equipment;
  const byStatus: Record<string, number> = {};
  eq.forEach((e) => {
    byStatus[e.status] = (byStatus[e.status] || 0) + 1;
  });
  const totalValue = eq.reduce((s, e) => s + e.purchasePrice, 0);
  return {
    totalEquipment: eq.length,
    byStatus,
    byCategory: state.categories.map((c) => ({ categoryId: c.id, categoryName: c.name, count: c.equipmentCount })),
    byLocation: state.locations.map((l) => ({ locationId: l.id, locationName: l.name, count: l.equipmentCount })),
    totalValue,
    averageValue: Math.round(totalValue / eq.length),
    recentlyAdded: 6,
  };
}

function equipmentHistory(id: string) {
  const txns = state.transactions.filter((t) => t.equipmentId === id);
  return {
    transactions: txns,
    maintenanceRequests: txns.filter((t) => t.type === 'MAINTENANCE'),
    statusChanges: txns.filter((t) => t.newStatus),
  };
}

/** Route a single demo request. `path` is the full request path (may include /api and query). */
export function handleDemoRequest(
  method: string,
  path: string,
  body: any,
  transport: Transport = 'axios',
): DemoResponse {
  // Normalize: drop everything up to and including '/api', split query.
  const apiIdx = path.indexOf('/api');
  let p = apiIdx >= 0 ? path.slice(apiIdx + 4) : path;
  const qIdx = p.indexOf('?');
  const query = new URLSearchParams(qIdx >= 0 ? p.slice(qIdx + 1) : '');
  if (qIdx >= 0) p = p.slice(0, qIdx);
  p = p.replace(/\/+$/, '') || '/';
  const m = method.toUpperCase();

  // ---- Auth ----
  if (p === '/auth/login' || p === '/auth/register') return ok(authPayload());
  if (p === '/auth/verify' || p === '/auth/me' || p === '/auth/profile') return ok(authPayload());
  if (p === '/auth/logout') return ok({ success: true });

  // ---- Equipment ----
  if (p === '/equipment/statistics') return ok(statistics());
  if (p === '/equipment/search') {
    const { equipment } = filterEquipment(query);
    return ok({
      equipment: equipment.slice(0, parseInt(query.get('limit') || '10', 10)).map((e) => projectEquipment(e, transport)),
    });
  }
  const histMatch = p.match(/^\/equipment\/([^/]+)\/history$/);
  if (histMatch) return ok(equipmentHistory(histMatch[1]));

  // ---- Import / Export (ImportExport page) ----
  // These must precede the single-segment `/equipment/:id` match below so that
  // `/equipment/import` isn't swallowed as an id lookup.
  if (p === '/equipment/import/validate' && m === 'POST') {
    const totalRows = 12;
    return ok({
      summary: { totalRows, validRows: totalRows, errorRows: 0 },
      canImport: true,
      errors: [],
      preview: state.equipment.slice(0, 5).map((e) => ({ name: e.name, category: e.category, status: 'AVAILABLE' })),
      note: 'Demo mode — validation is simulated.',
    });
  }
  if (p === '/equipment/import' && m === 'POST') {
    const totalRows = 12;
    return ok({
      summary: { totalRows, validRows: totalRows, errorRows: 0 },
      canImport: true,
      imported: totalRows,
      note: 'Demo mode — import is simulated and not persisted.',
    });
  }
  const exportMatch = p.match(/^\/equipment\/export\/(csv|excel)$/);
  if (exportMatch) {
    const header = 'Name,Category,Location,Status,Condition,Brand,Serial,QR Code,Purchase Price';
    const rows = state.equipment.map((e) =>
      [e.name, e.category, e.location, operationalStatus(e), e.condition, e.brand, e.serialNumber, e.qrCode, e.purchasePrice]
        .map((v) => `"${String(v ?? '')}"`)
        .join(','),
    );
    return raw([header, ...rows].join('\n'), 'text/csv');
  }
  const templateMatch = p.match(/^\/equipment\/template\/(csv|excel)$/);
  if (templateMatch) {
    return raw(
      'Name,Category,Location,Brand,Model,Serial,Purchase Price,Purchase Date,Condition\n"Example Basketball","Basketball","Main Gym","Spalding","TF-1000","ABC-123","79.99","2025-09-01","GOOD"',
      'text/csv',
    );
  }

  const checkoutMatch = p.match(/^\/equipment\/([^/]+)\/checkout$/);
  if (checkoutMatch && m === 'POST') {
    const eq = state.equipment.find((e) => e.id === checkoutMatch[1]);
    if (eq) {
      eq.checkedOutTo = body?.userName || 'Demo Athlete';
      eq.assignedTo = eq.checkedOutTo;
      eq.checkedOutDate = new Date().toISOString();
      eq.dueDate = body?.dueDate || new Date(Date.now() + 14 * 864e5).toISOString();
      eq.updatedAt = new Date().toISOString();
    }
    return ok({ success: true, equipment: eq });
  }
  const checkinMatch = p.match(/^\/equipment\/([^/]+)\/checkin$/);
  if (checkinMatch && m === 'POST') {
    const eq = state.equipment.find((e) => e.id === checkinMatch[1]);
    if (eq) {
      eq.checkedOutTo = undefined;
      eq.assignedTo = undefined;
      eq.checkedOutDate = undefined;
      eq.dueDate = undefined;
      if (body?.condition) eq.condition = body.condition;
      eq.updatedAt = new Date().toISOString();
    }
    return ok({ success: true, equipment: eq });
  }
  const statusMatch = p.match(/^\/equipment\/([^/]+)\/status$/);
  if (statusMatch && (m === 'PATCH' || m === 'PUT')) {
    const eq = state.equipment.find((e) => e.id === statusMatch[1]);
    if (eq && body?.status) {
      eq.status = body.status;
      eq.updatedAt = new Date().toISOString();
    }
    return ok(eq || {});
  }
  const idMatch = p.match(/^\/equipment\/([^/]+)$/);
  if (idMatch) {
    const eq = state.equipment.find((e) => e.id === idMatch[1] || e.qrCode === idMatch[1]);
    if (m === 'PUT' && eq) {
      Object.assign(eq, body || {}, { updatedAt: new Date().toISOString() });
    }
    return ok(eq ? projectEquipment(eq, transport) : {});
  }
  if (p === '/equipment') {
    if (m === 'POST') return ok({ success: true });
    const { equipment, total, page, limit } = filterEquipment(query);
    return ok({
      equipment: equipment.map((e) => projectEquipment(e, transport)),
      pagination: { page, limit, total, pages: Math.max(1, Math.ceil(total / limit)) },
    });
  }

  // ---- Categories / Locations ----
  // Match the real API's envelope ({ categories: [...] } / { locations: [...] });
  // list consumers unwrap it via EquipmentService or read the keyed field directly.
  if (p === '/categories') {
    return ok(
      m === 'GET'
        ? { categories: state.categories, pagination: { page: 1, limit: 100, total: state.categories.length, totalPages: 1 } }
        : { success: true },
    );
  }
  if (p.startsWith('/categories/')) return ok({ success: true });
  if (p === '/locations') {
    return ok(
      m === 'GET'
        ? { locations: state.locations, pagination: { page: 1, limit: 100, total: state.locations.length, totalPages: 1 } }
        : { success: true },
    );
  }
  if (p.startsWith('/locations/')) return ok({ success: true });

  // ---- Reports ----
  if (p === '/reports/dashboard') return ok(dashboard());
  if (p.startsWith('/reports/')) return ok({ data: [], generatedAt: new Date().toISOString() });

  // ---- Admin / users ----
  if (p === '/admin/users' || p === '/users') return ok({ users: state.staff, pagination: { page: 1, limit: 50, total: state.staff.length, pages: 1 } });
  if (p === '/admin/auth/config') {
    // Full shape the AuthConfig page expects — one card per provider plus SAML.
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const oauthProvider = (redirectPath: string) => ({
      enabled: false,
      configured: false,
      demoMode: true,
      clientId: '',
      clientSecret: '',
      redirectUri: `${origin}${redirectPath}`,
    });
    return ok({
      oauth2: {
        google: oauthProvider('/api/auth/google/callback'),
        microsoft: { ...oauthProvider('/api/auth/microsoft/callback'), tenantId: 'common' },
        github: oauthProvider('/api/auth/github/callback'),
      },
      saml: {
        enabled: false,
        configured: false,
        demoMode: true,
        entityId: '',
        ssoUrl: '',
        certificate: '',
        nameIdFormat: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
      },
    });
  }
  if (p.startsWith('/admin/auth/')) return ok({ success: true });

  // ---- Language / settings / notifications ----
  if (p === '/language/preferences') return ok({ language: 'en', timezone: 'America/Los_Angeles', dateFormat: 'MM/DD/YYYY' });
  if (p === '/settings/email') return ok({ smtpConfigured: false, reminderDays: [1, 3, 7], notifications: { overdue: true, maintenance: true } });
  if (p.startsWith('/notifications')) return ok({ success: true, sent: 0 });

  // ---- Voice ----
  if (p.startsWith('/voice')) return ok({ success: true, intent: 'UNKNOWN', confidence: 0, suggestions: [] });

  // ---- Health & fallthrough ----
  if (p === '/health' || p === '/health/ready') return ok({ status: 'ok', demo: true });
  return ok({ success: true });
}
